"use client";

import { useState, useCallback } from "react";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { authService } from "@/services/auth.service";
import { useAuthStore } from "@/store/auth.store";
import { resetSessionDead } from "@/lib/api-client";
import type {
  SendOtpPayload,
  VerifyOtpPayload,
  VerifyOtpResponse,
  RegisterPayload,
  AuthTokens,
  OtpMethod,
} from "@/types";

export type AuthStep = "phone" | "otp" | "register";

interface UseAuthFlowReturn {
  step: AuthStep;
  phone: string;
  setStep: (step: AuthStep) => void;
  sendOtp: UseMutationResult<void, Error, SendOtpPayload>;
  verifyOtp: UseMutationResult<VerifyOtpResponse, Error, VerifyOtpPayload>;
  register: UseMutationResult<VerifyOtpResponse, Error, { payload: RegisterPayload; token: string }>;
  handleSendOtp: (phone: string, method?: OtpMethod) => void;
  handleVerifyOtp: (otp: string) => void;
  handleRegister: (payload: RegisterPayload) => void;
  reset: () => void;
}

export function useAuthFlow(onSuccess?: () => void): UseAuthFlowReturn {
  const [step, setStep] = useState<AuthStep>("phone");
  const [phone, setPhone] = useState("");

  const { setRegisterToken, setUser } = useAuthStore();

  const sendOtp = useMutation({
    mutationFn: (payload: SendOtpPayload) => {
      return authService.sendOtp({ ...payload, lang: "fr" });
    },
    onSuccess: () => {
      setStep("otp");
    },
    onError: (err) => console.error("[auth] sendOtp ✗", err),
  });

  const verifyOtp = useMutation({
    mutationFn: (payload: VerifyOtpPayload) => {
      return authService.verifyOtp(payload);
    },
    onSuccess: (data) => {
      if ((data.isNewUser || data.needsRegistration) && data.registerToken) {
        setRegisterToken(data.registerToken);
        setStep("register");
      } else {
        // Backend sets httpOnly cookies; fetch profile via proxy
        resetSessionDead();
        authService.getProfile().then((profile) => {
          setUser(profile);
          onSuccess?.();
        }).catch((err) => console.error("[auth] getProfile ✗", err));
      }
    },
    onError: (err) => console.error("[auth] verifyOtp ✗", err),
  });

  const registerMutation = useMutation({
    mutationFn: ({ payload, token }: { payload: RegisterPayload; token: string }) => {
      return authService.register(payload, token);
    },
    onSuccess: () => {
      // Backend sets httpOnly cookies; fetch profile via proxy
      resetSessionDead();
      authService.getProfile().then((profile) => {
        setUser(profile);
        onSuccess?.();
      }).catch((err) => console.error("[auth] getProfile ✗", err));
    },
    onError: (err) => console.error("[auth] register ✗", err),
  });

  const handleSendOtp = useCallback(
    (phoneNumber: string, method: OtpMethod = "whatsapp") => {
      setPhone(phoneNumber);
      sendOtp.mutate({ phone: phoneNumber, type: method });
    },
    [sendOtp]
  );

  const handleVerifyOtp = useCallback(
    (otp: string) => {
      verifyOtp.mutate({ phone, otp });
    },
    [phone, verifyOtp]
  );

  const handleRegister = useCallback(
    (payload: RegisterPayload) => {
      const registerToken = useAuthStore.getState().registerToken;
      if (!registerToken) return;
      registerMutation.mutate({ payload, token: registerToken });
    },
    [registerMutation]
  );

  const reset = useCallback(() => {
    setStep("phone");
    setPhone("");
  }, []);

  return {
    step,
    phone,
    setStep,
    sendOtp,
    verifyOtp,
    register: registerMutation,
    handleSendOtp,
    handleVerifyOtp,
    handleRegister,
    reset,
  };
}
